import { event as fullStoryEvent } from '@fullstory/browser';
import { MetricEvent, MetricsTransport } from '@prophecy/metrics';

import { makeRestCall } from '../../data/rest/makeRestCall';
import { hostUrl } from '../../utils/getServerUrl';

const FLUSH_INTERVAL_MS = 5 * 1000; // 5 seconds
const MINUTE_MS = 60 * 1000;

export class FetchMetricsTransport implements MetricsTransport {
  private queue: MetricEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onPageHide = () => void this.flush(true);

  constructor(private endpoint = '/api/logs/events') {
    this.timer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    window.addEventListener('pagehide', this.onPageHide);
  }

  send(event: MetricEvent): Promise<void> {
    // Freeze to prevent caller-side mutations.
    this.queue.push(Object.freeze({ ...event }));
    return Promise.resolve();
  }

  // Splits events into per-minute groups so a single request never spans a minute boundary
  // (e.g. events at 11:59:50 and 12:00:10 must never be sent in the same batch).
  private groupByMinuteBucket(events: MetricEvent[]): MetricEvent[][] {
    const buckets = new Map<number, MetricEvent[]>();
    for (const event of events) {
      const bucket = Math.floor(new Date(event.time).getTime() / MINUTE_MS);
      const group = buckets.get(bucket);
      if (group) {
        group.push(event);
      } else {
        buckets.set(bucket, [event]);
      }
    }
    return Array.from(buckets.values());
  }

  private async flush(onUnload = false): Promise<void> {
    if (this.queue.length === 0) return;

    // Atomically drain queue; new events go to the next batch.
    const batch = this.queue.splice(0);
    const groups = this.groupByMinuteBucket(batch);

    await Promise.all(groups.map((group) => this.sendBatch(group, onUnload)));
  }

  private async sendBatch(batch: MetricEvent[], keepalive: boolean): Promise<void> {
    const batchJson = batch.map((e) => JSON.stringify(e));

    try {
      // keepalive lets the request outlive page unload instead of being aborted mid-flight.
      await makeRestCall(`${hostUrl}${this.endpoint}`, {
        method: 'POST',
        body: JSON.stringify(batchJson),
        keepalive
      });
    } catch {
      // Metric delivery failures must never propagate — silently drop the batch.
    }
  }

  // Stops the flush timer and unload listener when replacing or tearing down the transport.
  destroy(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    window.removeEventListener('pagehide', this.onPageHide);
  }
}

// Pipes every tracked event into FullStory
export class FullStoryTransport implements MetricsTransport {
  async send(event: MetricEvent): Promise<void> {
    try {
      fullStoryEvent(event.eventName, event);
    } catch {}
  }
}
